/**
 * The conversation slots and the parked list (A7Y-4981), shown on Accounts and,
 * since A7Y-5210, edited on This week (`editable`), each replaced whole as the
 * MCP's `replace_slots` and `replace_parked` do.
 *
 * A7Y-5342: a slot's note is written once and goes stale ("nudge by 9/09" on
 * 14 Oct), so each slot leads with its account's live state from the record —
 * stage, last touch, next date and the current next action — and the note
 * follows, dated by its own text (A7Y-5492); a past next date reads "was due".
 */
import { helmOneLine, helmTextDate } from '@a7y/platform-health/helm';
import type { CrmAccountListItem, CrmParkedEntry, CrmSlotDoc } from '@a7y/types';
import { isoDate } from './accountView';
import { shortDate } from './board';
import { ParkedEditor, SlotsEditor, Toggle } from './CrmEditors';
import { AccountNameLink, InlineMarkdown } from './CrmMarkdown';
import { resolveAccount } from './scoreboard';

/** The account a slot names, live from the accounts list, or null when it names none or an unknown one. */
function liveAccount(
  accountId: string | null,
  accounts: readonly CrmAccountListItem[]
): CrmAccountListItem | null {
  if (accountId === null) return null;
  return (
    accounts.find((i) => i.account.id === accountId || i.account.aliases.includes(accountId)) ??
    null
  );
}

function LiveState({ item, today }: { item: CrmAccountListItem; today: string }) {
  const { account } = item;
  const parts = [
    account.stage,
    account.lastTouch ? `last touch ${shortDate(account.lastTouch, today)}` : 'no last touch',
    // A7Y-5492: a next date in the past reads "was due", as the Board's does.
    item.nextDate
      ? item.nextDate < today
        ? `was due ${shortDate(item.nextDate, today)}`
        : `next ${shortDate(item.nextDate, today)}`
      : 'no next date',
  ];
  return (
    <span className="block text-xs text-zinc-600 dark:text-zinc-400" data-testid="crm-slot-live">
      <span className="capitalize">{parts.join(' · ')}</span>
      {account.nextAction ? (
        <span className="block text-zinc-800 dark:text-zinc-200" data-testid="crm-slot-next-action">
          {helmOneLine(account.nextAction)}
        </span>
      ) : null}
    </span>
  );
}

/**
 * A slot's note, dated by its own text (A7Y-5492): every slot was saved on 14 Oct by
 * the import, so the save date said nothing about when the words were written. A note
 * that names no date shows none.
 */
function SlotNote({ why, savedAt, today }: { why: string; savedAt: string; today: string }) {
  const written = helmTextDate(why, savedAt);
  return (
    <span className="block text-xs text-zinc-500" data-testid="crm-slot-note">
      {written ? `Slot note, ${shortDate(written, today)}: ` : 'Slot note: '}
      <InlineMarkdown text={why} />
    </span>
  );
}

function AccountLabel({
  accountId,
  accounts,
}: {
  accountId: string | null;
  accounts: readonly CrmAccountListItem[];
}) {
  const ref = resolveAccount(accountId, accounts);
  if (ref.accountId === null) return <span className="text-zinc-500">no account</span>;
  if (ref.name !== null) return <AccountNameLink accountId={ref.accountId} name={ref.name} />;
  return (
    <span>
      <code className="text-xs">{ref.accountId}</code>{' '}
      <span className="text-amber-700 dark:text-amber-400">(unknown account)</span>
    </span>
  );
}

export function SlotsPanel({
  slots,
  parked,
  accounts,
  editable = false,
}: {
  slots: CrmSlotDoc[];
  parked: CrmParkedEntry[];
  accounts: readonly CrmAccountListItem[];
  /** This week edits them (A7Y-5210); Accounts shows them. */
  editable?: boolean;
}) {
  const ordered = [...slots].sort((a, b) => a.slot - b.slot);
  const today = isoDate(new Date());
  return (
    <section aria-label="Conversation slots" className="grid gap-6 px-2 md:grid-cols-2">
      <div>
        <h2 className="mb-2 text-sm font-semibold text-zinc-900 dark:text-white">
          Active conversation slots
        </h2>
        {editable ? (
          <div className="mb-2">
            <Toggle label="Edit slots">
              {(close) => <SlotsEditor slots={slots} accounts={accounts} onClose={close} />}
            </Toggle>
          </div>
        ) : null}
        {ordered.length === 0 ? (
          <p className="text-sm text-zinc-500">No active slots.</p>
        ) : (
          <ol
            data-testid="crm-slots"
            className="space-y-1 text-sm text-zinc-900 dark:text-zinc-100"
          >
            {ordered.map((slot) => (
              <li key={slot.id} data-testid="crm-slot" className="space-y-0.5">
                <span className="tabular-nums text-zinc-500">{slot.slot}.</span>{' '}
                <AccountLabel accountId={slot.accountId} accounts={accounts} />
                {(() => {
                  const live = liveAccount(slot.accountId, accounts);
                  return live ? <LiveState item={live} today={today} /> : null;
                })()}
                {slot.why ? (
                  <SlotNote why={slot.why} savedAt={slot.updatedAt} today={today} />
                ) : null}
              </li>
            ))}
          </ol>
        )}
      </div>
      <div>
        <h2 className="mb-2 text-sm font-semibold text-zinc-900 dark:text-white">Parked</h2>
        {editable ? (
          <div className="mb-2">
            <Toggle label="Edit parked">
              {(close) => <ParkedEditor parked={parked} accounts={accounts} onClose={close} />}
            </Toggle>
          </div>
        ) : null}
        {parked.length === 0 ? (
          <p className="text-sm text-zinc-500">Nothing parked.</p>
        ) : (
          <ul
            data-testid="crm-parked"
            className="space-y-1 text-sm text-zinc-900 dark:text-zinc-100"
          >
            {parked.map((entry) => (
              <li key={entry.accountId} data-testid="crm-parked-entry">
                <AccountLabel accountId={entry.accountId} accounts={accounts} />
                {entry.why ? (
                  <span className="text-zinc-600 dark:text-zinc-400">
                    {' — '}
                    <InlineMarkdown text={entry.why} />
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
